import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {Dialog, DialogContent, DialogTitle, IconButton} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PdfViewer from "./PdfViewer";
import MedicalRules from "./MedicalRules";
import {getVneuronAdjudication} from "../../utils/API";
import {EnvKeys, getEnvItem} from "../../utils/EnvMetaDataUtil";
import "./MedicalAdmissibility.css";

/**
 * Medical admissibility - a two pane evidence review screen.
 *
 * Left: what the vNeuron engine found in the claim's documents, grouped into accordions.
 * Right: the document itself, scrolled to and highlighted at the exact spot backing whichever
 * evidence row was clicked.
 *
 * The adjudication is fetched here, from `claim/{insurerClaimIdentifier}/vneuron`, when the screen mounts.
 * The vNeuron tab renders this component conditionally, so mounting IS the tab being
 * clicked - there is no separate "is the tab open" signal to react to. A caller that
 * already holds the claim can pass `claimDetails` instead and no request is made.
 *
 * The rule-detail modal's body is `MedicalRules`, which renders the cards
 * `medicalRulesService` builds out of the same adjudication object this screen reads.
 */

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

/** Order and labels of the accordions - rendered in THIS array order. */
const ALL_ENTITY_DATA = [
    {id: 'document', accordionHeader: 'Documents'},
    {id: 'procedures', accordionHeader: 'Procedure'},
    {id: 'diagnoses', accordionHeader: 'Diagnosis'},
    {id: 'clinicalAttribute', accordionHeader: 'Evidence'},
    {id: 'claimDetails', accordionHeader: 'Claim Details'},
    {id: 'pre_existing_diseases', accordionHeader: 'Pre Existing Diseases'},
];

/** Which adjudicationDetail keys can become accordions - claimDetails is synthetic. */
const RULE_KEY_LIST = ['procedures', 'diagnoses', 'document', 'clinicalAttribute', 'pre_existing_diseases'];

const ENTITY_NOT_FOUND_TEXT = {
    procedures: 'Procedure not found',
    diagnoses: 'Diagnoses not found',
    document: 'Document not found',
    clinicalAttribute: 'Evidence not found',
    pre_existing_diseases: 'Pre Existing Diseases not found',
};

const RULE_NOT_EXECUTED_REMARK = 'Rule could not be executed with the provided information';

/** What an always-shown accordion says when the engine never ran its rule. */
const NO_RULE_REMARK = {
    procedures: RULE_NOT_EXECUTED_REMARK,
    diagnoses: RULE_NOT_EXECUTED_REMARK,
    clinicalAttribute: 'There is no evidence rule for this procedure.',
};

/**
 * Procedure, Diagnosis and Evidence stay on screen even when the engine never ran the rule,
 * so the user reads WHY instead of a silently missing accordion.
 */
const ALWAYS_SHOWN_RULE_KEYS = ['procedures', 'diagnoses', 'clinicalAttribute'];

/**
 * The engine sends the evidence list as `clinicalAttribute` on some payloads and `evidence`
 * on others (the rules service reads `evidence`), so the accordion takes whichever is present.
 */
const readRuleList = (adj, key) => {
    if (!adj) {
        return null;
    }
    if (key === 'clinicalAttribute') {
        return adj.clinicalAttribute || adj.evidence || null;
    }
    return adj[key] || null;
};

/** Accordion id -> the rule object on the adjudication that backs it. */
const RULE_OBJECT_KEY = {
    procedures: 'procedureRule',
    diagnoses: 'diagnosisRule',
    clinicalAttribute: 'clinicalTermsRule',
};

/** True when the engine sent no rule (null, absent or `{}`) behind this accordion. */
const isRuleMissing = (adj, key) => {
    const rule = adj && adj[RULE_OBJECT_KEY[key]];
    return !rule || (typeof rule === 'object' && !Array.isArray(rule) && Object.keys(rule).length === 0);
};

const VISIBLE_ROW_LIMIT = 5;

/** The keys whose rows get a document reference backfilled from documents[0]. */
const DOCUMENT_BACKFILL_KEYS = ['procedures', 'diagnoses', 'document', 'clinicalAttribute'];

/** Entity id -> the rule card the info icon opens in the modal. */
const RULE_TYPE_BY_ENTITY = {
    diagnoses: 'Diagnosis Rule',
    document: 'Document Rule',
    procedures: 'Procedure Rule',
    clinicalAttribute: 'Clinical Terms Rule',
};
/**
 * Anything without a rule of its own shows the whole protocol. The Angular original passed
 * 'Protocol Rule' here while app-medical-rules only recognised 'Protocols', so the default
 * landed in the unfiltered branch and showed every rule - that is what users have, and
 * 'Protocols' is the value that produces it here.
 */
const DEFAULT_RULE_TYPE = 'Protocols';

/** The literal remark the engine writes on a clinical condition that failed. */
const CONDITION_NOT_MET = 'Condition not met';

const EMPTY_PDF = {url: '', fileName: ''};

/* ------------------------------------------------------------------ *
 * The API response
 * ------------------------------------------------------------------ */

/**
 * The note the endpoint sends instead of an adjudication, e.g.
 * `{success: false, data: "No VNeuron data found for claim: 17393"}`. `data` is a string
 * there and an object when there is a real payload, which is what tells the two apart.
 *
 * @returns {string|null} the note, or null when the payload is not one of these.
 */
const readEmptyNote = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }
    if (payload.success === false && typeof payload.data === 'string' && payload.data.trim()) {
        return payload.data.trim();
    }
    if (typeof payload.message === 'string' && payload.message.trim()) {
        return payload.message.trim();
    }
    return null;
};

/**
 * Turn whatever `claim/{claimId}/vneuron` returned into the claim record this screen reads.
 *
 * The screen wants `{id, vneuronDecision, vneuronRemarks, vneuronResponseDTO}` - the engine's
 * payload plus the decision the claim carries beside it. The endpoint may hand back either
 * that whole record or the engine payload on its own, so both are accepted: a payload
 * carrying `vneuronResponseDTO` is already the record, anything carrying the engine's own
 * `adjudicationResult`/`adjudicationDetails` is the payload and gets wrapped.
 *
 * The decision and remark are only defaulted from the engine's own words when the record did
 * not carry them - `adjudicationResult` is the engine's verdict, `vneuronDecision` the
 * claim's, and they are not always the same word.
 *
 * @returns {object|null} null when there is nothing usable, which the screen shows as empty.
 */
const normalizeVneuronResponse = (payload, claimId) => {
    // Unwrap the {success, data, message} envelope the rest of this API uses, however deep.
    let body = payload;
    while (body && typeof body === 'object' && body.data && !body.vneuronResponseDTO
        && !body.adjudicationDetails && !body.adjudicationResult) {
        body = body.data;
    }
    if (!body || typeof body !== 'object') {
        return null;
    }

    if (body.vneuronResponseDTO) {
        return {
            ...body,
            id: body.id || claimId,
            vneuronDecision: body.vneuronDecision ?? body.vneuronResponseDTO.adjudicationResult ?? null,
            vneuronRemarks: body.vneuronRemarks ?? body.vneuronResponseDTO.adjudicationReason ?? null,
        };
    }

    if (body.adjudicationDetails || body.adjudicationResult) {
        return {
            id: body.id || claimId,
            vneuronDecision: body.adjudicationResult ?? null,
            vneuronRemarks: body.adjudicationReason ?? null,
            vneuronResponseDTO: body,
        };
    }

    return null;
};

/**
 * The split between the two panes, as a percentage of the screen's width taken by the left
 * one. The bounds keep either pane from being dragged down to an unreadable sliver.
 */
const SPLIT_DEFAULT = 42;
const SPLIT_MIN = 25;
const SPLIT_MAX = 70;
/** How far one arrow-key press moves the divider. */
const SPLIT_KEY_STEP = 2;
/** Breathing room left under the panes once they have been sized to the viewport. */
const SPLIT_BOTTOM_GUTTER = 12;

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

/**
 * A row that stands in for a rule the engine never ran. Same shape as a real evidence row,
 * so every consumer downstream - sorting, rendering - treats it normally.
 * `name: null` is what makes the list render `remarks` as plain text instead of a
 * clickable green/red pill.
 */
const noRuleRow = (remarks) => ({
    name: null,
    entityId: null,
    standardName: null,
    documentUrl: '',
    fileName: '',
    referenceToDocument: 0,
    pageNumber: 0,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    pageWidth: 0,
    pageHeight: 0,
    foundIn: null,
    found: false,
    remarks,
    parent_category: null,
    medicalReasoning: null,
    icdcode: null,
});

/** Found rows first, not-found after, otherwise stable. Always sorts a COPY. */
const sortFoundFirst = (rows) => [...rows].sort((a, b) => {
    if (Boolean(a.found) === Boolean(b.found)) {
        return 0;
    }
    return a.found ? -1 : 1;
});

/**
 * The evidence rows that back one clicked row, turned into annotations the viewer can place.
 *
 * Every row in the same list carrying the same name is highlighted, not only the clicked one
 * - one finding can be stated in several places in the same document.
 */
const buildAnnotations = (list, name) => {
    if (!Array.isArray(list) || list.length === 0) {
        console.warn('MedicalAdmissibility: no source list to build annotations from', name);
        return [];
    }
    return list
        .filter(Boolean)
        .filter((item) => item.name === name)
        .map((item) => ({
            // x and width are fixed on purpose: the engine's x is unreliable, so the
            // highlight is drawn as a band across the text column rather than a box around
            // the phrase, which would land off the words it is pointing at.
            x: 50,
            width: 500,
            // Inverted here AND in the viewer's transformAnnotationCoordinates, exactly as the
            // Angular pipeline does - the two flips cancel, so the band lands at the engine's y
            // measured from the TOP of the page. That is the engine's real origin: on a page
            // it reads age (y 526), then diagnosis (555), then procedure (644), top to bottom.
            // Flipping only once mirrors every highlight about the middle of the page.
            y: item.pageHeight - (item.y + item.height),
            height: item.height,
            // pageNumber is 1-based on the row; the viewer's transform adds the 1 back.
            pageIndex: item.pageNumber ? item.pageNumber - 1 : 0,
            pageHeight: item.pageHeight,
            pageWidth: item.pageWidth,
            found: item.found,
            documentUrl: item.documentUrl,
            name: item.name,
        }));
};

/** The comment shown on the decision summary card. */
const getVneuronRemarks = (claim) => {
    if (!claim) {
        return 'Pending';
    }
    const vneuron = claim.vneuronResponseDTO;
    if (claim.vneuronDecision === 'QUERY' && vneuron) {
        if (vneuron.queryType === 'Information') {
            const entry = (vneuron.infoNotFound || []).find((item) => item && item.evidence);
            const list = (entry && entry.evidence) || [];
            return list.length ? `${claim.vneuronRemarks} : ${list.join(', ')}` : `${claim.vneuronRemarks} `;
        }
        // A document query carries its reason in the remarks alone.
        return claim.vneuronRemarks;
    }
    return claim.vneuronDecision === 'ACCEPTED' ? 'Accepted' : (claim.vneuronRemarks || 'Pending');
};

/** The banner listing every clinical condition the engine could not satisfy. */
const buildRejectInfo = (adj) => {
    const conditionFound = adj && adj.clinicalTermsRule && adj.clinicalTermsRule.conditionFound;
    const list = (Array.isArray(conditionFound) ? conditionFound : [])
        .filter(Boolean)
        // The match on the remark is exact and literal - that string is the engine's flag.
        .filter((item) => item.found === false && item.remarks === CONDITION_NOT_MET)
        .map((item) => ({heading: item.evidenceText, subheading: item.medicalReasoning}));
    return {isShowReject: list.length > 0, list};
};

/** The document the right pane opens on. First match wins. */
const pickInitialPdf = (adj, documents) => {
    // Note the lowercase `filename` - adjudication rows spell it that way, while the
    // documents[] array spells it `fileName`. Both occur in the same payload.
    const fromRow = (row) => (
        row && row.documentUrl && row.filename ? {url: row.documentUrl, fileName: row.filename} : null
    );
    if (adj) {
        const fromAdjudication = fromRow((adj.procedures || [])[0])
            || fromRow((adj.diagnoses || [])[0])
            || fromRow((adj.evidence || [])[0])
            || fromRow((adj.document || [])[0]);
        if (fromAdjudication) {
            return fromAdjudication;
        }
    }
    // GUARD: the original dereferenced documents[0] unguarded and threw on a claim that
    // carries no documents at all. Falling through leaves the viewer on its error state.
    const first = (documents || [])[0];
    if (first && first.original_document_url && first.fileName) {
        return {url: first.original_document_url, fileName: first.fileName};
    }
    return EMPTY_PDF;
};

/**
 * Everything the screen reads, derived from the claim in one pass.
 * Nothing in here may throw on a half-populated claim.
 */
const buildEntities = (claimDetails) => {
    const vneuron = (claimDetails && claimDetails.vneuronResponseDTO) || null;
    // Only the first procedure code is ever adjudicated against; a claim carrying several
    // still reads from [0]. Intentional, and the same as the Angular original.
    const procedureCode = (vneuron && vneuron.procedureCodes && vneuron.procedureCodes[0]) || '';
    const adj = (vneuron && vneuron.adjudicationDetails && vneuron.adjudicationDetails[procedureCode]) || null;
    const documents = (vneuron && vneuron.documents) || [];

    const includedIds = ['claimDetails'];
    RULE_KEY_LIST.forEach((key) => {
        if (readRuleList(adj, key) || ALWAYS_SHOWN_RULE_KEYS.indexOf(key) !== -1) {
            includedIds.push(key);
        }
    });

    const firstDocument = documents[0] || null;

    const entities = ALL_ENTITY_DATA
        .filter((entity) => includedIds.indexOf(entity.id) !== -1)
        .map((entity) => {
            // The Claim Details accordion is a document picker, not evidence. The original
            // also required four unrelated accordions to exist before showing it, which
            // silently hid the picker on claims where only one rule had run; it is gated on
            // the document list alone now.
            if (entity.id === 'claimDetails') {
                const rows = documents.length > 0
                    ? documents.map((doc, index) => ({
                        name: `${index + 1}. ${doc.fileName || 'Document'} PDF`,
                        documentUrl: doc.original_document_url,
                        fileName: doc.fileName,
                    }))
                    : [];
                return {...entity, rows, rawList: []};
            }

            const rawList = readRuleList(adj, entity.id) || [];
            let rows = rawList.filter(Boolean);

            if (ALWAYS_SHOWN_RULE_KEYS.indexOf(entity.id) !== -1 && isRuleMissing(adj, entity.id)) {
                // The engine sent no rule for this accordion. One row saying so - `name: null`
                // renders the remark instead of a clickable pill.
                rows = [noRuleRow(NO_RULE_REMARK[entity.id] || RULE_NOT_EXECUTED_REMARK)];
            } else if (rows.length === 0 && rawList.length > 0) {
                // The engine ran the rule and found nothing - the first entry carries the why.
                rows = [{name: (rawList[0] && rawList[0].remarks) || ENTITY_NOT_FOUND_TEXT[entity.id]}];
            } else if (rows.length === 0 && ALWAYS_SHOWN_RULE_KEYS.indexOf(entity.id) !== -1) {
                // The rule is there but nothing came back for it.
                rows = [noRuleRow(ENTITY_NOT_FOUND_TEXT[entity.id])];
            }

            // Backfill the document reference on rows the engine left without one - done on
            // copies, where the original mutated the payload in place. Nullish coalescing, so
            // a deliberately empty string survives.
            if (DOCUMENT_BACKFILL_KEYS.indexOf(entity.id) !== -1 && firstDocument) {
                rows = rows.map((row) => ({
                    ...row,
                    documentUrl: row.documentUrl ?? firstDocument.original_document_url,
                    fileName: row.fileName ?? firstDocument.fileName,
                }));
            }

            return {...entity, rows, rawList};
        });

    return {
        vneuron,
        procedureCode,
        adj,
        entities,
        ruleId: (adj && adj.ruleId) || null,
        rulesData: adj,
        rejectInfo: buildRejectInfo(adj),
        initialPdf: pickInitialPdf(adj, documents),
    };
};

/**
 * The procedures as entered on the claim form - not what the engine found in the documents.
 * Only the Primary procedure is shown; secondary levels stay on the claim form.
 */
const buildDefinedProcedureRows = (summary) => {
    if (!summary) {
        return {rows: [], icd: '', hasIcd: false};
    }
    const procedures = summary.procedures || [];
    const primary = procedures.find((item) => item && Number(item.procedureLevel) === 1) || procedures[0] || null;

    let rows = [];
    if (primary) {
        const row = {
            levelName: primary.procedureLevelName || 'Primary',
            name: primary.procedureName || '',
            code: primary.procedureCode || '',
            icdCodeLabel: primary.icdCodeLabel || '',
        };
        if (row.name || row.code || row.icdCodeLabel) {
            rows = [row];
        }
    }

    if (rows.length === 0) {
        // The header string can read "Colostomy ✚ 2" - the suffix is a count of the other
        // procedures on the claim, not part of the name, so it is cut off here.
        const name = (summary.primaryName || summary.procedureName || '').split('✚')[0].trim();
        if (name) {
            rows = [{levelName: 'Primary', name, code: '', icdCodeLabel: ''}];
        }
    }

    // The summary's ICD label belongs to the Primary procedure, so it is dropped when the
    // rows already carry their own - otherwise the same code would be printed twice.
    const icd = rows.some((row) => row.icdCodeLabel) ? '' : (summary.icdCodeLabel || '');
    return {rows, icd, hasIcd: Boolean(icd) || rows.some((row) => row.icdCodeLabel)};
};

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

function MedicalAdmissibility(props) {
    const {claimId, insurerClaimIdentifier, claimDetails, definedProcedure, t} = props;

    // The claim as fetched. A `claimDetails` prop wins over it, so a caller that already has
    // the record - or a test - keeps working without the request being made at all.
    const [fetchedClaim, setFetchedClaim] = useState(null);
    const [loadState, setLoadState] = useState(claimDetails ? 'ready' : 'loading');
    const [loadError, setLoadError] = useState(null);
    // What the server said when it had no adjudication to give, shown as-is on the pane.
    const [emptyNote, setEmptyNote] = useState(null);
    // Bumped by the Retry button - the only reason to re-run the fetch for the same claim.
    const [retryToken, setRetryToken] = useState(0);

    const claim = claimDetails || fetchedClaim;

    // Mounting is the tab being clicked; the identifier changing is a different claim - or the
    // claim record arriving, since the identifier comes from it.
    useEffect(() => {
        if (claimDetails || !insurerClaimIdentifier) {
            // Nothing to fetch: either the record was handed in, or there is no claim yet.
            setLoadState(claimDetails ? 'ready' : 'idle');
            return undefined;
        }
        // Guards against a resolved request for a claim the user has already navigated off.
        let live = true;
        setLoadState('loading');
        setLoadError(null);
        setEmptyNote(null);
        getVneuronAdjudication(insurerClaimIdentifier)
            .then((payload) => {
                if (!live) {
                    return;
                }
                const record = normalizeVneuronResponse(payload, claimId);
                setFetchedClaim(record);
                // Nothing usable came back. That is a normal answer for a claim the engine
                // has not run, so it gets its own state rather than being dressed as a
                // failure - there is nothing here to retry away.
                setEmptyNote(record ? null : readEmptyNote(payload));
                setLoadState(record ? 'ready' : 'empty');
            })
            .catch((error) => {
                if (!live) {
                    return;
                }
                // getVneuronAdjudication has already shown the toast; this is the pane's copy.
                console.error('MedicalAdmissibility: could not load the adjudication', error);
                setFetchedClaim(null);
                setEmptyNote(null);
                setLoadError(error && error.message ? error.message : 'Could not load medical admissibility.');
                setLoadState('error');
            });
        return () => {
            live = false;
        };
    }, [claimId, insurerClaimIdentifier, claimDetails, retryToken]);

    const derived = useMemo(() => buildEntities(claim), [claim]);
    const {vneuron, entities, rulesData, rejectInfo, initialPdf} = derived;

    // The engine returns its document urls encrypted, so they are fetched back through the
    // vNeuron backend rather than straight off S3 - PdfViewer builds that request itself and
    // only needs to be told where to send it and what to sign it with.
    const pdfApiBaseUrl = useMemo(() => getEnvItem(EnvKeys.vneuronBackendUrl) || '', []);
    const getPdfAuthToken = useCallback(() => localStorage.getItem('authToken'), []);

    // `definedProcedure` arrives after the claim and is re-supplied, so the two "defined on
    // claim" boxes recompute on their own without disturbing anything else on the screen.
    const {
        rows: definedProcedureRows,
        icd: definedProcedureIcd,
        hasIcd: hasDefinedDiagnosisIcd,
    } = useMemo(() => buildDefinedProcedureRows(definedProcedure), [definedProcedure]);

    const [openEntityId, setOpenEntityId] = useState(() => (entities[0] ? entities[0].id : null));
    // FIX vs the Angular original, which compared a row index against a row object and so
    // never highlighted anything. The key is `${entityId}:${rowIndex}`.
    const [selectedRowKey, setSelectedRowKey] = useState(null);
    const [pdf, setPdf] = useState(() => initialPdf);
    const [annotations, setAnnotations] = useState([]);
    const [scrollTarget, setScrollTarget] = useState(null);
    // One global flag, shared by every accordion - a single `+` expands all the lists at
    // once. That is how the original behaves and it is deliberate.
    const [expandAllRows, setExpandAllRows] = useState(false);
    const [ruleModalType, setRuleModalType] = useState(null);

    // How much of the split the details pane takes. The divider writes to it while dragged.
    const [splitPercent, setSplitPercent] = useState(SPLIT_DEFAULT);

    const claimKey = claim ? claim.id : null;
    const userPickedPdfRef = useRef(false);
    const rootRef = useRef(null);
    const splitRef = useRef(null);
    const draggingRef = useRef(false);

    // A new claim starts the screen over.
    useEffect(() => {
        userPickedPdfRef.current = false;
        setOpenEntityId(entities[0] ? entities[0].id : null);
        setSelectedRowKey(null);
        setAnnotations([]);
        setScrollTarget(null);
        setExpandAllRows(false);
        setRuleModalType(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [claimKey]);

    // The pane follows the derivation until the user picks a document themselves.
    useEffect(() => {
        if (!userPickedPdfRef.current) {
            setPdf(initialPdf);
        }
    }, [initialPdf]);

    /**
     * Size the panes to whatever is left of the window below them.
     *
     * The tab sits under a claim header whose height depends on the claim, so the space the
     * split gets cannot be a constant in the stylesheet - it is measured and written to
     * --ma-chrome, which the CSS subtracts from the viewport height. The reading is taken
     * from the top of the DOCUMENT, so it does not change with how far the page is scrolled.
     */
    useEffect(() => {
        const root = rootRef.current;
        const split = splitRef.current;
        if (!root || !split) {
            return undefined;
        }
        let lastChrome = null;
        let frame = null;
        const measure = () => {
            const offset = split.getBoundingClientRect().top + window.scrollY + SPLIT_BOTTOM_GUTTER;
            const chrome = `${Math.round(offset)}px`;
            // Writing the same value again would resize the body for nothing and re-fire the observer.
            if (chrome !== lastChrome) {
                lastChrome = chrome;
                root.style.setProperty('--ma-chrome', chrome);
            }
        };
        // Resizing the panes resizes the body, so measuring inside the observer callback would
        // feed the observer in the same frame ("ResizeObserver loop completed..."). Defer a frame.
        const scheduleMeasure = () => {
            if (frame !== null) {
                return;
            }
            frame = window.requestAnimationFrame(() => {
                frame = null;
                measure();
            });
        };
        measure();
        window.addEventListener('resize', scheduleMeasure);
        // The header above can grow or shrink after this runs - a late claim response, a wrapped
        // line, the claim info accordion - and the panes have to follow it.
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleMeasure) : null;
        if (observer) {
            observer.observe(document.body);
        }
        return () => {
            window.removeEventListener('resize', scheduleMeasure);
            if (observer) {
                observer.disconnect();
            }
            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }
        };
    }, [claimKey]);

    /** Put the divider wherever the pointer is, clamped, as a share of the split's width. */
    const applyPointer = useCallback((clientX) => {
        const node = splitRef.current;
        if (!node) {
            return;
        }
        const rect = node.getBoundingClientRect();
        if (rect.width === 0) {
            return;
        }
        const percent = ((clientX - rect.left) / rect.width) * 100;
        setSplitPercent(Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, percent)));
    }, []);

    // The listeners live on the window, not the divider: the pointer regularly leaves the
    // 6px handle mid-drag, and over the PDF canvas at that, which would end the drag early.
    useEffect(() => {
        const stopDragging = () => {
            if (draggingRef.current) {
                draggingRef.current = false;
                document.body.classList.remove('maResizing');
            }
        };
        const handleMove = (event) => {
            if (draggingRef.current) {
                // Without this the browser starts a text selection across both panes.
                event.preventDefault();
                applyPointer(event.clientX);
            }
        };
        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', stopDragging);
        window.addEventListener('pointercancel', stopDragging);
        return () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', stopDragging);
            window.removeEventListener('pointercancel', stopDragging);
            stopDragging();
        };
    }, [applyPointer]);

    const startDragging = useCallback((event) => {
        draggingRef.current = true;
        // The class holds the resize cursor and kills selection everywhere for the drag.
        document.body.classList.add('maResizing');
        applyPointer(event.clientX);
    }, [applyPointer]);

    const handleResizerKeyDown = useCallback((event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            const step = event.key === 'ArrowLeft' ? -SPLIT_KEY_STEP : SPLIT_KEY_STEP;
            setSplitPercent((current) => Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, current + step)));
        } else if (event.key === 'Home') {
            event.preventDefault();
            setSplitPercent(SPLIT_DEFAULT);
        }
    }, []);

    const toggleEntity = useCallback((entityId) => {
        setOpenEntityId((current) => (current === entityId ? null : entityId));
    }, []);

    const openRuleModal = useCallback((event, entityId) => {
        // The icon sits inside the accordion header, so its click must not toggle the panel.
        event.stopPropagation();
        setRuleModalType(RULE_TYPE_BY_ENTITY[entityId] || DEFAULT_RULE_TYPE);
    }, []);

    const handleRowClick = useCallback((entity, row, rowIndex) => {
        setSelectedRowKey(`${entity.id}:${rowIndex}`);

        // The rule-not-executed row and the stubs point at no document: inert, not a crash.
        if (!row.documentUrl) {
            setAnnotations([]);
            setScrollTarget(null);
            return;
        }

        // Read the matches off the raw adjudication list, not the display rows, so stub and
        // backfilled rows never end up in the highlight set.
        const next = buildAnnotations(entity.rawList, row.name);
        setAnnotations(next);
        // A new array identity every click, so re-clicking the same row scrolls again.
        setScrollTarget(next.length > 0 ? next : null);
        // A row click swaps the url and carries the current file name forward, as the
        // original does - the name labels the pane, the url is what is fetched.
        setPdf((current) => ({url: row.documentUrl, fileName: row.fileName || current.fileName}));
        userPickedPdfRef.current = true;
    }, []);

    // With no adjudication there is nothing to lay out: `buildEntities` still returns the
    // Procedure and Diagnosis accordions - they are the two that stay on screen so a reviewer
    // reads WHY a rule did not run - and on an empty claim that is a skeleton of stubs
    // standing in for a page. The pane shows the empty state instead.
    const hasAdjudication = Boolean(vneuron);

    /** The tone every status surface on the screen keys off: badge, dot, tint, border. */
    const decisionTone = useMemo(() => {
        const result = vneuron && vneuron.adjudicationResult;
        if (result === 'APPROVED') {
            return 'ok';
        }
        if (result === 'REJECTED') {
            return 'bad';
        }
        if (result === 'QUERY') {
            return 'warn';
        }
        return 'neutral';
    }, [vneuron]);

    return (
        <div className="medicalAdmissibility" ref={rootRef}>
            <section className="maShell">
                <header className="maTopBar">
                    <h2 className="maTitle">{t('MEDICAL_ADMISSIBILITY.MEDICAL_ADMISSIBILITY')}</h2>
                    {hasAdjudication && (
                        <span className="maBadge" data-tone={decisionTone}>
                            <span className="maBadgeDot" aria-hidden="true"/>
                            {vneuron.adjudicationResult || 'PENDING'}
                        </span>
                    )}
                </header>

                {/* The two panes are pinned to the viewport and scroll independently: the
                    evidence list is long, and scrolling it must not carry the document away. */}
                <div className={`maSplit ${hasAdjudication ? '' : 'is-empty'}`} ref={splitRef}>
                    <div className="maPane maPaneDetails" style={{flexBasis: `${splitPercent}%`}}>
                        <div className="maPaneScroll">
                            {!hasAdjudication ? (
                                <div className="maEmpty">
                                    {loadState === 'loading' ? (
                                        <>
                                            <span className="maSpinner" aria-hidden="true"/>
                                            <span className="maEmptyTitle">Loading medical admissibility…</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className={`maEmptyIcon ${loadState === 'error' ? 'is-error' : ''}`}
                                                  aria-hidden="true">
                                                {loadState === 'error' ? (
                                                    <svg viewBox="0 0 24 24" width="22" height="22"
                                                         fill="currentColor">
                                                        <path d="M12 2 1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2V9h2v4z"/>
                                                    </svg>
                                                ) : (
                                                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
                                                         stroke="currentColor" strokeWidth="1.8"
                                                         strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/>
                                                        <polyline points="14 3 14 8 19 8"/>
                                                        <line x1="9" y1="13" x2="15" y2="13"/>
                                                        <line x1="9" y1="17" x2="13" y2="17"/>
                                                    </svg>
                                                )}
                                            </span>

                                            <span className="maEmptyTitle">
                                                {loadState === 'error' && 'Could not load medical admissibility'}
                                                {loadState === 'idle' && 'No claim selected'}
                                                {(loadState === 'empty' || loadState === 'ready')
                                                    && 'No medical admissibility for this claim'}
                                            </span>

                                            {/* The server's own words when it had any, so a
                                                reviewer sees what it actually said. */}
                                            {loadState === 'error' && loadError && (
                                                <span className="maEmptyNote">{loadError}</span>
                                            )}
                                            {loadState === 'empty' && (
                                                <span className="maEmptyNote">
                                                    {emptyNote || 'The engine has not adjudicated this claim yet.'}
                                                </span>
                                            )}
                                            {loadState === 'ready' && (
                                                <span className="maEmptyNote">
                                                    The engine has not adjudicated this claim yet.
                                                </span>
                                            )}

                                            {/* Only offered where it can change the answer -
                                                an adjudication can land later, and a failed
                                                request can succeed. "No claim" cannot. */}
                                            {(loadState === 'error' || loadState === 'empty') && insurerClaimIdentifier && (
                                                <button
                                                    type="button"
                                                    className="maBtn"
                                                    onClick={() => setRetryToken((current) => current + 1)}
                                                >
                                                    {loadState === 'error' ? 'Retry' : 'Check again'}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {rejectInfo.isShowReject && (
                                        <section className="maReject">
                                            <div className="maRejectHead">
                                                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"
                                                     aria-hidden="true">
                                                    <path d="M12 2 1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2V9h2v4z"/>
                                                </svg>
                                                Conditions not met
                                            </div>
                                            <ul className="maRejectList">
                                                {rejectInfo.list.map((item, index) => (
                                                    <li key={`reject-${index}`}>
                                                        <span className="maRejectHeading">{item.heading}</span>
                                                        {item.subheading && (
                                                            <span className="maRejectReason">{item.subheading}</span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </section>
                                    )}
                                </>
                            )}

                            {hasAdjudication && entities.map((entity) => {
                                const isOpen = openEntityId === entity.id;
                                const sorted = sortFoundFirst(entity.rows);
                                const visible = expandAllRows ? sorted : sorted.slice(0, VISIBLE_ROW_LIMIT);
                                const isDocumentPicker = entity.id === 'claimDetails';
                                // Only real evidence is counted - the stub rows carry no verdict,
                                // so a "2/5 found" on them would be a number about nothing.
                                const scored = isDocumentPicker ? [] : entity.rows.filter((row) => row.name);
                                const foundCount = scored.filter((row) => row.found).length;
                                const hiddenCount = sorted.length - VISIBLE_ROW_LIMIT;

                                return (
                                    <section className={`maCard ${isOpen ? 'is-open' : ''}`} key={entity.id}>
                                        <div className="maCardHead">
                                            <button
                                                type="button"
                                                className="maCardToggle"
                                                aria-expanded={isOpen}
                                                onClick={() => toggleEntity(entity.id)}
                                            >
                                                <svg className="maChevron" viewBox="0 0 24 24" width="16" height="16"
                                                     fill="none" stroke="currentColor" strokeWidth="2.5"
                                                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <polyline points="9 6 15 12 9 18"/>
                                                </svg>
                                                <span className="maCardTitle">{entity.accordionHeader}</span>
                                                {scored.length > 0 && (
                                                    <span
                                                        className="maCount"
                                                        data-tone={foundCount === scored.length ? 'ok' : 'bad'}
                                                        title={`${foundCount} of ${scored.length} found`}
                                                    >
                                                        {foundCount}/{scored.length}
                                                    </span>
                                                )}
                                                {isDocumentPicker && entity.rows.length > 0 && (
                                                    <span className="maCount" data-tone="neutral">
                                                        {entity.rows.length}
                                                    </span>
                                                )}
                                            </button>
                                            {/* The document picker has no rule behind it. */}
                                            {!isDocumentPicker && (
                                                <button
                                                    type="button"
                                                    className="maInfoBtn"
                                                    title="Rule detail"
                                                    aria-label={`Rule detail for ${entity.accordionHeader}`}
                                                    onClick={(event) => openRuleModal(event, entity.id)}
                                                >
                                                    <svg width="17" height="17" viewBox="0 0 24 24"
                                                         fill="currentColor" aria-hidden="true">
                                                        <path
                                                            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                                                    </svg>
                                                </button>
                                            )}
                                        </div>

                                        {isOpen && (
                                            <div className="maCardBody">
                                                {entity.id === 'procedures' && definedProcedureRows.length > 0 && (
                                                    <div className="maDefined">
                                                        <span className="maEyebrow">Procedure defined on claim</span>
                                                        {definedProcedureRows.map((row, index) => (
                                                            <div className="maDefinedRow"
                                                                 key={`defined-procedure-${index}`}>
                                                                <span className="maChip">{row.levelName}</span>
                                                                <span className="maDefinedName">{row.name || '—'}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {entity.id === 'diagnoses' && hasDefinedDiagnosisIcd && (
                                                    <div className="maDefined">
                                                        <span className="maEyebrow">Diagnosis defined on claim</span>
                                                        {definedProcedureRows.map((row, index) => (
                                                            row.icdCodeLabel ? (
                                                                <div className="maDefinedIcd"
                                                                     key={`defined-icd-${index}`}>
                                                                    ICD : {row.icdCodeLabel}
                                                                </div>
                                                            ) : null
                                                        ))}
                                                        {definedProcedureIcd && (
                                                            <div className="maDefinedIcd">
                                                                ICD : {definedProcedureIcd}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                <ul className="maRows">
                                                    {visible.map((row, rowIndex) => {
                                                        const rowKey = `${entity.id}:${rowIndex}`;
                                                        // Claim Details rows are document links, not
                                                        // pass/fail evidence, so they carry no verdict.
                                                        const tone = isDocumentPicker
                                                            ? 'doc'
                                                            : (row.found ? 'ok' : 'bad');
                                                        // A message-only row points at no document, so
                                                        // it is plain text rather than a button.
                                                        if (!row.name) {
                                                            return (
                                                                <li key={rowKey}>
                                                                    <div className="maRow is-static" data-tone="muted">
                                                                        <span className="maRowText">
                                                                            <span className="maRowReason">{row.remarks}</span>
                                                                        </span>
                                                                    </div>
                                                                </li>
                                                            );
                                                        }
                                                        return (
                                                            <li key={rowKey}>
                                                                <button
                                                                    type="button"
                                                                    className={`maRow ${selectedRowKey === rowKey ? 'is-active' : ''}`}
                                                                    data-tone={tone}
                                                                    onClick={() => handleRowClick(entity, row, rowIndex)}
                                                                >
                                                                    <span className="maRowMark" aria-hidden="true"/>
                                                                    {/* One line - "discharge summary : Document
                                                                        discharge summary is present in the claim". */}
                                                                    <span className="maRowText is-inline">
                                                                        <span className="maRowName">{row.name}</span>
                                                                        {row.medicalReasoning && (
                                                                            <span className="maRowReason">
                                                                                {` : ${row.medicalReasoning}`}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                </button>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>

                                                {hiddenCount > 0 && (
                                                    <button
                                                        type="button"
                                                        className="maMoreBtn"
                                                        data-expanded={expandAllRows}
                                                        onClick={() => setExpandAllRows((current) => !current)}
                                                    >
                                                        {expandAllRows ? 'Show less' : `Show ${hiddenCount} more`}
                                                        <svg className="maChevron" viewBox="0 0 24 24" width="14"
                                                             height="14" fill="none" stroke="currentColor"
                                                             strokeWidth="2.5" strokeLinecap="round"
                                                             strokeLinejoin="round" aria-hidden="true">
                                                            <polyline points="9 6 15 12 9 18"/>
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </section>
                                );
                            })}

                            {hasAdjudication && (
                                <section className="maSummary" aria-label="Medical admissibility decision">
                                    <h3 className="maSummaryTitle">
                                        {t('MEDICAL_ADMISSIBILITY.MEDICAL_ADMISSIBILITY')}
                                    </h3>
                                    <dl className="maSummaryList">
                                        <div className="maSummaryRow">
                                            <dt>Decision :</dt>
                                            <dd>{claim.vneuronDecision || '—'}</dd>
                                        </div>
                                        <div className="maSummaryRow">
                                            <dt>Comment :</dt>
                                            <dd>{getVneuronRemarks(claim)}</dd>
                                        </div>
                                    </dl>
                                </section>
                            )}
                        </div>
                    </div>

                    <div
                        className="maResizer"
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize the details and document panes"
                        aria-valuenow={Math.round(splitPercent)}
                        aria-valuemin={SPLIT_MIN}
                        aria-valuemax={SPLIT_MAX}
                        tabIndex={0}
                        onPointerDown={startDragging}
                        onKeyDown={handleResizerKeyDown}
                    >
                        <span className="maResizerGrip" aria-hidden="true"/>
                    </div>

                    <div className="maPane maPanePdf">
                        {/* The file name was only ever a tooltip on the pane; a reviewer
                            jumping between documents needs to see which one is open. */}
                        <div className="maDocBar">
                            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 7V3.5L18.5 9H13z"/>
                            </svg>
                            <span className={`maDocName ${pdf.fileName ? '' : 'is-placeholder'}`}
                                  title={pdf.fileName}>
                                {pdf.fileName || 'No document selected'}
                            </span>
                        </div>
                        <div className="maDocFrame">
                            {/* PdfViewer's own fallback is a red "Unable to Access PDF", which
                                is the wrong thing to say about a claim that simply has no
                                document to open - so it is not mounted without one. */}
                            {pdf.url ? (
                                // The pane owns the height now - the viewer fills whatever the
                                // split gives it instead of the fixed page-sized box it had.
                                <PdfViewer
                                    pdfUrl={pdf.url}
                                    fileName={pdf.fileName}
                                    coordinates={annotations}
                                    scrollTarget={scrollTarget}
                                    claimNumber={claim ? claim.id : ''}
                                    docType="medical"
                                    apiBaseUrl={pdfApiBaseUrl}
                                    getAuthToken={getPdfAuthToken}
                                    width="100%"
                                    height="100%"
                                />
                            ) : (
                                <div className="maDocEmpty">
                                    <svg viewBox="0 0 24 24" width="26" height="26" fill="none"
                                         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
                                         strokeLinejoin="round" aria-hidden="true">
                                        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/>
                                        <polyline points="14 3 14 8 19 8"/>
                                    </svg>
                                    <span className="maEmptyTitle">No document to display</span>
                                    <span className="maEmptyNote">
                                        {hasAdjudication
                                            ? 'Select a finding on the left to open the document behind it.'
                                            : 'Documents appear here once the claim has been adjudicated.'}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <Dialog
                open={ruleModalType !== null}
                onClose={() => setRuleModalType(null)}
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        boxShadow: '0 24px 48px -12px rgba(16, 24, 40, .25)',
                        overflow: 'hidden',
                    },
                }}
            >
                {/* MedicalRules prints the "Protocols" heading and the rule's own name, so the
                    title bar carries only the close button - naming the rule here too said it twice. */}
                <DialogTitle sx={{display: 'flex', justifyContent: 'flex-end', p: 1, pb: 0}}>
                    <IconButton size="small" aria-label="Close" onClick={() => setRuleModalType(null)}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{px: 2.5, pt: 0, pb: 2.5}}>
                    {ruleModalType !== null && (
                        <MedicalRules
                            jsonData={rulesData}
                            type={ruleModalType}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

MedicalAdmissibility.propTypes = {
    claimId: PropTypes.string,
    insurerClaimIdentifier: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    claimDetails: PropTypes.object,
    definedProcedure: PropTypes.object,
    t: PropTypes.func,
};

MedicalAdmissibility.defaultProps = {
    claimId: null,
    insurerClaimIdentifier: null,
    claimDetails: null,
    definedProcedure: null,
    // No i18n is wired up in this app yet - the key's last segment reads close enough to the
    // English label until one is.
    t: (key) => String(key).split('.').pop(),
};

export default MedicalAdmissibility;
export {buildEntities, buildDefinedProcedureRows, buildAnnotations, getVneuronRemarks, sortFoundFirst};
